import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card, CardContent } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { formatCivilDate, formatHours, STATUS_LABELS } from '../requests/format.js';
import { Pagination, PastLastPage, StatusFilter, TABLE, TD, TH, useListFilters, usePageHref, useRequestQueryFilters } from '../requests/request-list.js';
import { RequestSearchForm } from '../requests/request-search.js';
import { useScopedRequestsPage } from '../requests/requests-api.js';
import { StatusBadge } from '../requests/status-badge.js';
import { useMe } from '../session/session.js';

/**
 * Mis solicitudes: una página de `GET /requests?scope=mine`, con estado y los filtros de H-02 en la URL.
 * Con el aspecto de la de la Power App (`src/pages/my-requests-page.tsx`): cabecera, filtros y tabla en
 * tarjetas, e insignias con los colores `status-*`.
 */
export function MyRequestsPage() {
  const me = useMe();
  const [filters, setStatus] = useListFilters();
  const [search, applySearch] = useRequestQueryFilters();
  const pageHref = usePageHref('pagina');
  const result = useScopedRequestsPage('mine', { ...search, areaId: '', status: filters.status, page: filters.page });
  const { status } = filters;

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <PageHeader
        title="Mis solicitudes"
        description="Busca, filtra y consulta el estado de tus permisos."
        action={
          me.canRequest ? (
            <Link to="/solicitudes/nueva" className={buttonClasses()}>
              <Plus aria-hidden className="size-4" />
              Nueva solicitud
            </Link>
          ) : null
        }
      />
      <Card>
        <CardContent className="space-y-4">
          <StatusFilter status={status} onChange={setStatus} className="" />
          <RequestSearchForm key={Object.values(search).join('\u0000')} filters={search} onApply={applySearch} showArea={false} className="" />
        </CardContent>
      </Card>
      <div>
        {result.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
        {result.isError ? <ApiErrorView error={result.error} /> : null}
        {result.isSuccess && result.data.total === 0 ? (
          <Card className="items-center py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {status === undefined ? 'Todavía no tienes solicitudes.' : `No tienes solicitudes en estado «${STATUS_LABELS[status]}».`}
            </p>
          </Card>
        ) : null}
        {result.isSuccess && result.data.total > 0 ? (
          <>
            {result.data.items.length === 0 ? (
              <Card className="px-5">
                <PastLastPage firstPage={pageHref(1)} />
              </Card>
            ) : (
              <Card flush>
                <div className="overflow-x-auto">
                  <table className={TABLE}>
                    <thead className="border-b border-border bg-muted text-muted-foreground">
                      <tr>
                        <th scope="col" className={TH}>Solicitud</th>
                        <th scope="col" className={TH}>Fecha del permiso</th>
                        <th scope="col" className={TH}>Horario</th>
                        <th scope="col" className={TH}>Horas solicitadas</th>
                        <th scope="col" className={TH}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.data.items.map((request) => (
                        <tr key={request.id} className="border-t border-border transition-colors first:border-t-0 hover:bg-muted">
                          <th scope="row" className={TH}>
                            <Link to={`/solicitudes/${request.id}`} className="text-primary underline-offset-4 hover:underline">
                              {request.requestNumber}
                            </Link>
                          </th>
                          <td className={TD}>{formatCivilDate(request.permissionDate)}</td>
                          <td className={TD}>
                            {request.departureTime}–{request.returnTime}
                          </td>
                          <td className={TD}>{formatHours(request.requestedHours)}</td>
                          <td className={TD}>
                            <StatusBadge status={request.status} tokens />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
            <Pagination page={filters.page} total={result.data.total} pageSize={result.data.pageSize} hrefFor={pageHref} />
          </>
        ) : null}
      </div>
    </div>
  );
}
